import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import {
  animate,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faWandMagicSparkles, faArrowRight, faCheck, faCloudArrowUp } from '@fortawesome/free-solid-svg-icons';
import { firstValueFrom, interval, Subscription } from 'rxjs';
import { CdkDragDrop, moveItemInArray, DragDropModule } from '@angular/cdk/drag-drop';
import {
  AuthenticationService,
  NotificationService,
  AmplitudeService,
  QuestionService,
  QuestionTemplateService,
  UploadService,
  LicenseService,
} from '@core/services';
import { ConfirmDialogComponent, QualifyDemoComponent, BattlecardsDemoComponent } from '@core/component';
import { UserConfigService, type CrmProvider } from '@core/services';
import { OnboardingService, type OnboardingProgress } from '@core/services/onboarding/onboarding.service';

interface QuestionRow {
  text: string;
  category: string;
  originalText?: string; // Track original text to detect edits
}

interface FileMetadata {
  id?: string;
  name: string;
  storagePath?: string;
  note?: string;
  size?: number;
  type?: string;
  status?: string;
  error_message?: string;
  isUploading?: boolean;
  uploadError?: string;
  originalFile?: File;
  originalNote?: string;
}

type PromptTemplateKey = 'BANT' | 'MEDDPICC' | 'SPIN' | '15-min call';

const FRAMEWORK_QUESTIONS: Record<string, QuestionRow[]> = {
  BANT: [
    // { text: 'What is your budget range for this solution?', category: 'Budget' },
    // { text: 'Who are the key decision-makers in this process?', category: 'Authority' },
    // { text: 'What are the top challenges you are trying to address?', category: 'Need' },
    // { text: 'What is your implementation timeline?', category: 'Timing' },
    // { text: 'What happens if you don\'t finalize anything by that timeline?', category: 'Timing' },
  ],
  MEDDPICC: [
    // { text: 'How are you currently measuring success?', category: 'Metrics' },
    // { text: 'What is the business impact of these challenges?', category: 'Economic Buyer' },
    // { text: 'What does your decision process look like?', category: 'Decision Process' },
    // { text: 'What criteria are you using to evaluate solutions?', category: 'Decision Criteria' },
    // { text: 'Who else are you evaluating along with us?', category: 'Competition' },
  ],
  SPIN: [
    // { text: 'What does your current workflow look like?', category: 'Situation' },
    // { text: 'What are the biggest pain points in that process?', category: 'Problem' },
    // { text: 'What happens if those pain points go unresolved?', category: 'Implication' },
    // { text: 'How would solving this impact your team\'s performance?', category: 'Need-Payoff' },
    // { text: 'What would an ideal solution look like for you?', category: 'Need-Payoff' },
  ],
  Custom: [],
};

const PROMPT_TEMPLATES: Record<PromptTemplateKey, string> = {
  BANT: 'BANT',
  MEDDPICC: 'MEDDPICC',
  SPIN: 'SPIN',
  '15-min call': '15-minute discovery call',
};

const ROLES = ['AE', 'BDR', 'CSM', 'Renewals', 'Alliances', 'Other'];
const FRAMEWORKS = ['BANT', 'MEDDPICC', 'SPIN', 'Custom'];
const TOTAL_STEPS = 5;
const FREE_EMAIL_DOMAINS = [
  'gmail.com', 'yahoo.com', 'outlook.com', 'hotmail.com',
  'icloud.com', 'aol.com', 'protonmail.com', 'mail.com',
  'zoho.com', 'yandex.com', 'gmx.com', 'live.com'
];

@Component({
  selector: 'app-onboarding',
  standalone: true,
  imports: [CommonModule, FormsModule, ConfirmDialogComponent, FontAwesomeModule, DragDropModule, QualifyDemoComponent, BattlecardsDemoComponent, FontAwesomeModule],
  templateUrl: './onboarding.component.html',
  styleUrls: ['./onboarding.component.scss'],
  animations: [
    trigger('stepTunnel', [
      transition(':increment', [
        style({ opacity: 0, transform: 'translateY(10px)' }),
        animate('220ms cubic-bezier(0.16, 1, 0.3, 1)', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),
      transition(':decrement', [
        style({ opacity: 0, transform: 'translateY(10px)' }),
        animate('220ms cubic-bezier(0.16, 1, 0.3, 1)', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),
    ]),
  ],
})
export class OnboardingComponent implements OnInit, AfterViewInit {
  faWandMagicSparkles = faWandMagicSparkles;
  faArrowRight = faArrowRight;
  faCheck = faCheck;
  faCloudArrowUp = faCloudArrowUp;
  @ViewChild('generateConfirmModal') generateConfirmModal!: ConfirmDialogComponent;
  @ViewChild('qualifyDemo') qualifyDemo!: QualifyDemoComponent;
  @ViewChild('battlecardsDemo') battlecardsDemo!: BattlecardsDemoComponent;

  currentStep = 1;
  setupComplete = false;
  confettiArray = Array.from({ length: 50 });
  totalSteps = TOTAL_STEPS;

  // Step 1 — Role (now Workflows - can select up to 2)
  workflows: { key: string; title: string; subtitle: string }[] = [
    { key: 'closing', title: 'Closing', subtitle: 'AE / AM' },
    { key: 'initiating', title: 'Initiating', subtitle: 'BDR / SDR' },
    { key: 'retention', title: 'Retention', subtitle: 'CSM / Renewals' },
    { key: 'partnering', title: 'Partnering', subtitle: 'Alliances / Channel' },
    { key: 'technical', title: 'Technical', subtitle: 'SE / Solutions' },
    { key: 'management', title: 'Management', subtitle: 'Sales Leaders / RVPs' },
  ];
  selectedWorkflows: string[] = [];
  maxWorkflowSelection = 2;

  // Step 2 — Framework
  frameworks = FRAMEWORKS;
  selectedFramework: string | null = null;

  // Step 3 — Questions (single-screen flow)
  productDescription = '';
  callDuration: '15' | '30' | '45' | '60+' = '30';
  questions: QuestionRow[] = [];
  newQuestionText = '';
  generatePrompt = '';
  isGenerating = false;
  selectedPromptKey: PromptTemplateKey | null = null;
  hasGeneratedOnce = false; // Track if questions were generated
  inputsChanged = false; // Track if inputs changed after generation
  shouldAnimateQuestions = false;

  // Step 4 — Sources
  readonly MAX_PDFS = 3;
  uploadedFileCount = 0;
  isUploading = false;
  uploadedFiles: FileMetadata[] = [];
  pendingUploads: FileMetadata[] = [];
  webSources: string[] = [];
  newWebSource = '';
  userDefaultDomain: string | null = null;
  showFreeEmailTooltip = false;
  private statusPolling?: Subscription;
  private readonly statusPollInterval = 5000;

  // Step 5 — CRM
  crmProviders: { key: CrmProvider; label: string; colorClass: string }[] = [
    { key: 'hubspot', label: 'HubSpot', colorClass: 'crm-hubspot' },
    { key: 'salesforce', label: 'Salesforce', colorClass: 'crm-salesforce' },
    { key: 'zoho', label: 'Zoho', colorClass: 'crm-zoho' },
    // { key: 'pipedrive', label: 'Pipedrive', colorClass: 'crm-pipedrive' }, // Coming soon
  ];
  connectedCrmProvider: CrmProvider | null = null;
  isCrmConnecting = false;
  crmConnectingProvider: CrmProvider | null = null;

  // General
  isLoading = true;
  isSaving = false;
  isExitingDashboard = false;
  hasPendingInvite = false;
  pendingInviteOrgName = '';
  private _pendingGeneratePrompt = '';
  private readonly _minimumActionLoadingMs = 1000;

  constructor(
    private readonly _router: Router,
    private readonly _route: ActivatedRoute,
    private readonly _authService: AuthenticationService,
    private readonly _notificationService: NotificationService,
    private readonly _amplitudeService: AmplitudeService,
    private readonly _questionService: QuestionService,
    private readonly _questionTemplateService: QuestionTemplateService,
    private readonly _uploadService: UploadService,
    private readonly _userConfigService: UserConfigService,
    private readonly _onboardingService: OnboardingService,
    private readonly _licenseService: LicenseService
  ) {}

  async ngOnInit(): Promise<void> {
    if (this._route.snapshot.queryParams['showComplete'] === 'true') {
      this.setupComplete = true;
    }
    
    this.isLoading = true;
    try {
      const progress = await this._onboardingService.loadProgress();
      if (progress) {
        this._restoreProgress(progress);
      }
      this.userDefaultDomain = this._userConfigService.getUserDefaultDomain();
      this._refreshSelectedPrompt();
      // Load existing uploaded files count
      try {
        await this._loadFiles();
      } catch {
        this.uploadedFileCount = 0;
      }
      // Check CRM connection
      this.connectedCrmProvider = await this._userConfigService.loadConnectedCrmProvider();
      // Load existing web sources from config
      try {
        const bcConfig = await this._userConfigService.loadBattleCardDomains();
        if (bcConfig.domains.length > 0) {
          this.webSources = [...new Set([...this.webSources, ...bcConfig.domains])];
        }

        const defaultDomain = this.userDefaultDomain;
        const shouldAddDefaultDomain =
          !!defaultDomain &&
          !bcConfig.ignoreDefault &&
          !this._isFreeEmailDomain(defaultDomain);

        if (shouldAddDefaultDomain && defaultDomain && !this.webSources.includes(defaultDomain)) {
          this.webSources.push(defaultDomain);
        }
      } catch {}

      // Check for pending invites
      try {
        const inviteInfo = await this._licenseService.checkPendingInvite();
        this.hasPendingInvite = inviteInfo.has_invite;
        this.pendingInviteOrgName = inviteInfo.organization_name || 'an organization';
      } catch {}
      
      // Load step 3 state from localStorage
      this._loadStep3State();
    } catch (error) {
      console.error('Error loading onboarding state:', error);
    } finally {
      this.isLoading = false;
    }
  }

  ngAfterViewInit(): void {
    // Demos are now opened on-demand via ? button clicks, not automatically
  }

  private async _loadFiles(): Promise<void> {
    try {
      const files: any[] = await this._uploadService.getFiles();
      this.uploadedFiles = (files || []).map(file => ({
        id: file.id,
        name: file.file_name || this.getDisplayName(file.path || file.name || ''),
        storagePath: file.path || '',
        note: file.note || '',
        size: file.size,
        type: file.type,
        status: file.status,
        error_message: file.error_message,
      }));
      this.uploadedFileCount = this.uploadedFiles.length;
      this.syncStatusPolling();
    } catch (error) {
      console.error('Error loading files:', error);
      this.uploadedFiles = [];
      this.uploadedFileCount = 0;
    }
  }

  openQualifyDemo(): void {
    if (!this.qualifyDemo) {
      return;
    }

    this.qualifyDemo.open();
  }

  openBattlecardsDemo(): void {
    if (!this.battlecardsDemo) {
      return;
    }

    this.battlecardsDemo.open();
  }

  // --- Navigation ---

  /** Priority URLs the backend accepts for a single user. */
  private readonly _maxWebSources = 2;

  get canProceed(): boolean {
    switch (this.currentStep) {
      case 1: return true; // Workflows are skippable
      case 2: return true; // Framework is skippable
      case 3: return this.questions.length >= 1; // Must have at least 1 question
      // PDFs are optional. A URL still in the input counts as added: goNext
      // commits it, so the button must not be disabled while it sits there.
      case 4: return !this.isFreeEmailDomain || this.webSources.length > 0 || !!this.newWebSource.trim();
      case 5: return true; // CRM finish always available
      default: return false;
    }
  }

  get stepLabel(): string {
    switch (this.currentStep) {
      case 1: return 'What do you do most often?';
      case 2: return 'How do you usually qualify deals?';
      case 3: return 'Add your qualification questions';
      case 4: return 'Add your answer sources';
      case 5: return 'Connect your CRM';
      default: return '';
    }
  }

  get stepDescription(): string {
    switch (this.currentStep) {
      case 1: return 'We\'ll tailor questions, call guidance, and summaries to your workflow.';
      case 2: return 'Pick a framework to start with. You can edit any question later.';
      case 3: return 'Describe what you sell and call duration. convverse.ai will generate a draft question set.';
      case 4: return 'Add the URLs convverse should prioritize. Uploading your own PDFs is optional, but it makes answers faster and more accurate.';
      case 5: return 'Push call summary, qualification, and next steps directly into your CRM automatically.';
      default: return '';
    }
  }

  get stepSubDescription(): string {
    if (this.currentStep === 1) {
      return 'Pick up to 2 workflows.';
    }
    return '';
  }

  async goNext(): Promise<void> {
    // A URL still sitting in the input counts as intent to add it, so the user
    // does not have to press Add and then Next.
    if (this.currentStep === 4 && this.newWebSource.trim()) {
      // addWebSource surfaces its own validation message when it refuses.
      if (!this.addWebSource()) {
        if (this.webSources.length >= this._maxWebSources) {
          // At the cap the text can never be accepted, so holding Continue
          // hostage to it would be a dead end. Drop it and move on.
          this.newWebSource = '';
        } else {
          // A malformed URL is a typo worth fixing before moving on.
          return;
        }
      }
    }

    if (this.currentStep === 4 && this.isFreeEmailDomain && this.webSources.length === 0) {
      this._notificationService.notify('Please add a priority URL to continue with a free email domain.');
      return;
    }

    if (!this.canProceed || this.isSaving) return;

    const startedAt = Date.now();
    this.isSaving = true;
    try {
      await this._saveCurrentStep();
      await this._ensureMinimumLoadingTime(startedAt);
      if (this.currentStep < this.totalSteps) {
        this.currentStep++;
      }
    } catch (error) {
      console.error('Error saving step:', error);
      this._notificationService.notifyError('Failed to save progress');
    } finally {
      this.isSaving = false;
    }
  }

  goBack(): void {
    if (this.currentStep > 1) {
      this.currentStep--;
    }
  }

  async finishSetup(): Promise<void> {
    if (this.isSaving) return;

    const startedAt = Date.now();
    this.isSaving = true;
    try {
      await this._onboardingService.completeOnboarding();
      await this._ensureMinimumLoadingTime(startedAt);
      this._amplitudeService.trackEvent('onboarding_completed', {
        workflows: this.selectedWorkflows,
        framework: this.selectedFramework,
        questions_count: this.questions.length,
        files_uploaded: this.uploadedFileCount,
        web_sources_count: this.webSources.length,
        crm_connected: this.connectedCrmProvider,
      });
      this.isSaving = false;
      this._router.navigate(['/choose-plan'], { queryParams: { fromOnboarding: 'true' } });
    } catch (error) {
      console.error('Error completing onboarding:', error);
      this._notificationService.notifyError('Failed to complete setup');
      this.isSaving = false;
    }
  }

  goToDashboard(): void {
    this.isExitingDashboard = true;
    // Wait for animation to complete before navigating
    setTimeout(() => {
      this._router.navigate(['/dashboard']);
    }, 750);
  }

  // --- Step 1: Workflows ---

  toggleWorkflow(workflowKey: string): void {
    const index = this.selectedWorkflows.indexOf(workflowKey);
    if (index > -1) {
      // Already selected, remove it (allow deselection)
      this.selectedWorkflows.splice(index, 1);
    } else {
      // Not selected, add it if under limit
      if (this.selectedWorkflows.length < this.maxWorkflowSelection) {
        this.selectedWorkflows.push(workflowKey);
      } else {
        // At limit, replace the first selection
        this.selectedWorkflows.shift();
        this.selectedWorkflows.push(workflowKey);
      }
    }
  }

  isWorkflowSelected(workflowKey: string): boolean {
    return this.selectedWorkflows.includes(workflowKey);
  }

  // --- Step 2: Framework ---

  selectFramework(fw: string): void {
    this.selectedFramework = fw;
    this._applyDefaultPromptFromFramework(fw);
    // Don't pre-populate questions - let user generate them
    this.questions = [];
  }

  // --- Step 3: Questions ---

  selectCallDuration(duration: '15' | '30' | '45' | '60+'): void {
    this.callDuration = duration;
    this.onInputChange();
  }

  buildPromptFromInputs(): string {
    const workflowLabels = this.selectedWorkflows
      .map(key => {
        const workflow = this.workflows.find(w => w.key === key);
        return workflow ? `${workflow.title} (${workflow.subtitle})` : key;
      })
      .join(' and ');

    const companyName = this._getPromptCompanyName();
    const product = this.productDescription.trim();
    const duration = this.callDuration === '60+' ? '60+ minute' : `${this.callDuration} minute`;
    const framework = this.selectedFramework || 'BANT';
    const fallbackFramework = this._getDefaultFrameworkLabel();

    let prompt = `I work in ${workflowLabels || 'sales'}. `;
    if (product) {
      prompt += `We sell ${product}. `;
    } else {
      prompt += `I am from ${companyName}. ${fallbackFramework} I sell SaaS products. `;
    }
    prompt += `Generate ${framework} questions for a ${duration} discovery call.`;

    if (product && companyName && companyName !== 'Enterprise SaaS') {
      prompt += ` Company: ${companyName}.`;
    }

    return prompt;
  }

  async generateQuestionsFromInputs(): Promise<void> {
    // If regenerating (questions already exist), show confirmation
    if (this.questions.length > 0) {
      const prompt = this.buildPromptFromInputs();
      this._pendingGeneratePrompt = prompt;
      this.generateConfirmModal.open();
      return;
    }

    // First time generation - no confirmation needed
    const prompt = this.buildPromptFromInputs();
    await this._runGenerate(prompt);

    if (this.questions.length > 0) {
      this.hasGeneratedOnce = true;
      this.inputsChanged = false;
        this._triggerQuestionAnimation();
      await this._saveStep3State();
    }
  }

  onInputChange(): void {
    // Mark that inputs have changed after generation
    if (this.hasGeneratedOnce) {
      this.inputsChanged = true;
    }
  }

  get shouldShowGenerateButton(): boolean {
    // Show generate button if:
    // 1. No questions yet, OR
    // 2. Inputs changed after generation
    return this.questions.length === 0 || this.inputsChanged;
  }

  get processedFileCount(): number {
    return this.uploadedFiles.filter(file => this._isProcessedStatus(file.status)).length;
  }

  get connectedCrmLabel(): string {
    if (!this.connectedCrmProvider) return '';
    const match = this.crmProviders.find(provider => provider.key === this.connectedCrmProvider);
    return match?.label || this.connectedCrmProvider;
  }

  addQuestion(): void {
    const text = this.newQuestionText.trim();
    if (!text) return;
    this.questions.push({ text, category: 'Custom' });
    this.newQuestionText = '';
  }

  trackByQuestion(index: number, question: QuestionRow): QuestionRow {
    return question;
  }

  removeQuestion(index: number): void {
    this.questions.splice(index, 1);
  }

  onQuestionTextChange(question: QuestionRow): void {
    // If user edits a generated question, change tag to "Custom"
    if (question.originalText && question.text !== question.originalText) {
      question.category = 'Custom';
    }
  }

  moveQuestion(fromIndex: number, toIndex: number): void {
    if (fromIndex === toIndex) return;
    const item = this.questions[fromIndex];
    this.questions.splice(fromIndex, 1);
    this.questions.splice(toIndex, 0, item);
  }

  onQuestionDrop(event: CdkDragDrop<QuestionRow[]>): void {
    moveItemInArray(this.questions, event.previousIndex, event.currentIndex);
  }

  formatCategory(category: string): string {
    if (!category) return category;
    // Add space before capital letters (handling camelCase and PascalCase)
    return category
      .replace(/([a-z])([A-Z])/g, '$1 $2') // Add space between lowercase and uppercase
      .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2') // Handle consecutive capitals like "EconomicBuyer"
      .trim()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  private _triggerQuestionAnimation(): void {
    this.shouldAnimateQuestions = true;
    setTimeout(() => {
      this.shouldAnimateQuestions = false;
    }, 650);
  }

  async generateQuestions(): Promise<void> {
    const prompt = this.generatePrompt.trim();
    if (!prompt || this.isGenerating) return;

    if (this.questions.length > 0) {
      this._pendingGeneratePrompt = prompt;
      this.generateConfirmModal.open();
      return;
    }

    await this._runGenerate(prompt);
  }

  handleGenerateConfirm(confirmed: boolean): void {
    if (!confirmed) {
      this._pendingGeneratePrompt = '';
      return;
    }

    const prompt = this._pendingGeneratePrompt;
    this._pendingGeneratePrompt = '';
    if (!prompt) return;

    // Run generation and update state
    void this._runGenerate(prompt).then(() => {
      if (this.questions.length > 0) {
        this.hasGeneratedOnce = true;
        this.inputsChanged = false;
        void this._saveStep3State();
      }
    });
  }

  private async _runGenerate(prompt: string): Promise<void> {
    if (!prompt || this.isGenerating) return;

    this.isGenerating = true;
    try {
      const result = await firstValueFrom(
        this._questionTemplateService.generateQuestions(prompt)
      );
      const generated = result?.questions || [];
      if (generated.length > 0) {
        this.questions = generated.map((q: any) => ({
          text: q.question_text || q,
          category: q.question_tag || 'Generated',
          originalText: q.question_text || q, // Store original for edit detection
        }));
        this._notificationService.notify("Here's the draft questions, please edit or reorder!");
      }
    } catch (error: any) {
      console.error('Error generating questions:', error);
      this._notificationService.notifyError(error.message || 'Failed to generate questions');
    } finally {
      this.isGenerating = false;
    }
  }

  setGeneratePrompt(key: PromptTemplateKey): void {
    this.selectedPromptKey = key;
    this.generatePrompt = this._buildPrompt(key);
  }

  onGeneratePromptChange(): void {
    this.selectedPromptKey = null;
  }

  // --- Step 4: Sources ---

  private _isFreeEmailDomain(domain: string | null): boolean {
    if (!domain) return false;
    return FREE_EMAIL_DOMAINS.includes(domain.toLowerCase());
  }

  private _buildPrompt(templateKey: PromptTemplateKey): string {
    const companyName = this._getPromptCompanyName();
    const frameworkLabel = PROMPT_TEMPLATES[templateKey];
    return `Generate ${frameworkLabel} questions for ${companyName}`;
  }

  private _getPromptCompanyName(): string {
    const domain = this.userDefaultDomain;
    if (!domain || this._isFreeEmailDomain(domain)) {
      return 'Enterprise SaaS';
    }

    const parts = domain.split('.').filter(Boolean);
    if (parts.length === 0) {
      return 'Enterprise SaaS';
    }

    let base = '';
    if (parts.length >= 3 && parts[parts.length - 2] === 'co' && parts[parts.length - 1].length === 2) {
      base = parts[parts.length - 3];
    } else if (parts.length >= 2) {
      base = parts[parts.length - 2];
    } else {
      base = parts[0];
    }

    const cleaned = base.replace(/[-_]+/g, ' ').trim();
    if (!cleaned) {
      return 'Enterprise SaaS';
    }

    return cleaned
      .split(' ')
      .filter(Boolean)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  private _isPromptTemplateKey(value: string): value is PromptTemplateKey {
    return value === 'BANT' || value === 'MEDDPICC' || value === 'SPIN' || value === '15-min call';
  }

  private _applyDefaultPromptFromFramework(framework: string | null): void {
    if (framework && this._isPromptTemplateKey(framework)) {
      this.setGeneratePrompt(framework);
      return;
    }

    this.selectedPromptKey = null;
  }

  private _refreshSelectedPrompt(): void {
    if (!this.selectedPromptKey) return;
    this.generatePrompt = this._buildPrompt(this.selectedPromptKey);
  }

  private _getDefaultFrameworkLabel(): string {
    if (this.selectedFramework && this.selectedFramework !== 'Custom') {
      return this.selectedFramework;
    }
    return 'BANT';
  }

  // Exposed for template to show the same notice used in Settings
  get isFreeEmailDomain(): boolean {
    return this._isFreeEmailDomain(this.userDefaultDomain);
  }

  onFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      this._handleFiles(input.files);
    }
    // Reset so selecting the same file(s) again still fires (change).
    input.value = '';
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const files = event.dataTransfer?.files;
    if (files?.length) {
      this._handleFiles(files);
    }
  }

  private _isPdf(file: File): boolean {
    const name = (file.name || '').toLowerCase();
    return file.type === 'application/pdf' || name.endsWith('.pdf');
  }

  private _currentFileCount(): number {
    return this.uploadedFiles.length + this.pendingUploads.filter(p => p.status !== 'error').length;
  }

  private _handleFiles(fileList: FileList | File[]): void {
    const files = Array.from(fileList);
    const pdfs = files.filter(f => this._isPdf(f));

    if (pdfs.length < files.length) {
      this._notificationService.notify('Only PDF files can be uploaded.');
    }
    if (!pdfs.length) return;

    const remaining = this.MAX_PDFS - this._currentFileCount();
    if (remaining <= 0) {
      this._notificationService.notify(`You can upload up to ${this.MAX_PDFS} PDFs.`);
      return;
    }

    const accepted = pdfs.slice(0, remaining);
    if (pdfs.length > remaining) {
      this._notificationService.notify(`You can upload up to ${this.MAX_PDFS} PDFs. Only ${remaining} more can be added.`);
    }

    accepted.forEach(file => this._handleFileUpload(file));
  }

  /**
   * Committing is optional (Continue picks up whatever is in the box), so the
   * button reads as an "add another" affordance once a source already exists.
   */
  get addWebSourceLabel(): string {
    return this.webSources.length > 0 ? 'Add more' : 'Add URL';
  }

  /** @returns true when the input was accepted (or was empty), false on a validation error. */
  addWebSource(): boolean {
    const url = this.newWebSource.trim().toLowerCase();
    if (!url) return true;

    // Validate that it looks like a URL or domain
    const urlPattern = /^(https?:\/\/)?([\w\-]+\.)+[a-z]{2,}(\/.*)?$/i;
    if (!urlPattern.test(url)) {
      this._notificationService.notify('Please enter a valid URL or domain', 'error');
      return false;
    }

    // Limit to the maximum number of URLs
    if (this.webSources.length >= this._maxWebSources) {
      this._notificationService.notify(`You can only add up to ${this._maxWebSources} priority URLs`, 'error');
      return false;
    }

    if (this.webSources.includes(url)) {
      // Already captured — clear the box and let the caller carry on.
      this.newWebSource = '';
      this._notificationService.notify('URL already added', 'error');
      return true;
    }

    this.webSources.push(url);
    this.newWebSource = '';
    this.closeFreeEmailTooltip();
    return true;
  }

  removeWebSource(index: number): void {
    this.webSources.splice(index, 1);
  }

  // --- Step 5: CRM ---

  async connectCrm(provider: CrmProvider): Promise<void> {
    if (this.isCrmConnecting) return;

    this.isCrmConnecting = true;
    this.crmConnectingProvider = provider;

    try {
      const authUrl = await this._userConfigService.requestCrmConnectUrl(provider);
      this._amplitudeService.trackEvent('onboarding_crm_connect_started', { provider });
      // Redirect to CRM OAuth — on return, user lands back on /onboarding
      // and the onboarding component reloads CRM status
      globalThis.location.href = authUrl;
    } catch (error) {
      console.error(`Error connecting ${provider}:`, error);
      this._notificationService.notifyError(`Failed to connect ${provider}`);
      this.isCrmConnecting = false;
      this.crmConnectingProvider = null;
    }
  }

  isCrmConnected(provider: CrmProvider): boolean {
    return this.connectedCrmProvider === provider;
  }

  isCrmLocked(provider: CrmProvider): boolean {
    return !!this.connectedCrmProvider && this.connectedCrmProvider !== provider;
  }

  getCrmButtonLabel(provider: CrmProvider): string {
    if (this.isCrmConnecting && this.crmConnectingProvider === provider) {
      return 'Connecting...';
    }
    if (this.isCrmConnected(provider)) {
      return 'Connected';
    }
    return 'Connect';
  }

  // --- Internal ---

  private async _saveCurrentStep(): Promise<void> {
    switch (this.currentStep) {
      case 1:
        await this._onboardingService.saveStepCompletion(1, {
          workflows: this.selectedWorkflows.length > 0 ? this.selectedWorkflows : undefined,
        });
        break;
      case 2:
        await this._onboardingService.saveStepCompletion(2, {
          framework: this.selectedFramework || undefined,
        });
        break;
      case 3:
        await this._publishQuestions();
        await this._saveStep3State();
        await this._onboardingService.saveStepCompletion(3);
        break;
      case 4:
        await this._saveSources();
        await this._onboardingService.saveStepCompletion(4);
        break;
      case 5:
        // CRM step saved on finish
        break;
    }
  }

  private async _saveStep3State(): Promise<void> {
    // Save step 3 state to localStorage for persistence
    const state = {
      productDescription: this.productDescription,
      callDuration: this.callDuration,
      questions: this.questions,
      hasGeneratedOnce: this.hasGeneratedOnce,
      inputsChanged: this.inputsChanged,
    };
    localStorage.setItem('onboarding_step3_state', JSON.stringify(state));
  }

  private _loadStep3State(): void {
    try {
      const saved = localStorage.getItem('onboarding_step3_state');
      if (saved) {
        const state = JSON.parse(saved);
        this.productDescription = state.productDescription || '';
        this.callDuration = state.callDuration || '30';
        this.questions = state.questions || [];
        this.hasGeneratedOnce = state.hasGeneratedOnce || (this.questions.length > 0);
        this.inputsChanged = state.inputsChanged || false;
      }
    } catch (error) {
      console.error('Error loading step 3 state:', error);
    }
  }

  private async _publishQuestions(): Promise<void> {
    if (this.questions.length === 0) return;

    try {
      // Get default template
      const templates = await firstValueFrom(this._questionTemplateService.getTemplates());
      let templateId: string | null = null;

      if (templates && templates.length > 0) {
        const defaultTemplate = templates.find((t: any) => t.is_default);
        templateId = defaultTemplate?.id || templates[0]?.id || null;
      }

      if (!templateId) {
        // Create default template
        const created = await firstValueFrom(
          this._questionTemplateService.createTemplate('Default', true)
        );
        templateId = (created as any)?.[0]?.id || (created as any)?.id || null;
      }

      if (!templateId) {
        throw new Error('Could not resolve template');
      }

      // Get user id
      const userDetail = this._authService.getUserDetail();
      const userId = userDetail?.user?.id || (userDetail as any)?.id;

      // IMPORTANT: Delete existing questions from this template to avoid duplicates
      try {
        const existingQuestions: any[] = await firstValueFrom(
          this._questionService.loadQuestions(templateId)
        );
        if (existingQuestions && existingQuestions.length > 0) {
          // Collect all question IDs
          const questionIds = existingQuestions
            .map((q: any) => q.id)
            .filter((id: any) => id != null);
          
          if (questionIds.length > 0) {
            // Delete all existing questions in batch
            await firstValueFrom(this._questionService.deleteQuestions(questionIds));
          }
        }
      } catch (error) {
        console.warn('Error deleting existing questions:', error);
        // Continue anyway - we'll insert new ones
      }

      // Build question payload (batch insert)
      const questionPayload = this.questions.map((q, i) => ({
        user_id: userId,
        template_id: templateId,
        question_text: q.text,
        status: true,
        order: i + 1,
      }));

      await firstValueFrom(this._questionService.addNewQustion(questionPayload));
    } catch (error) {
      console.error('Error publishing questions:', error);
      throw error;
    }
  }

  private async _saveSources(): Promise<void> {
    try {
      const uniqueDomains = [...new Set(this.webSources.map(s => s.toLowerCase()))];
      await this._userConfigService.saveBattleCardDomains(false, uniqueDomains);
    } catch (error) {
      console.error('Error saving web sources:', error);
      throw error;
    }
  }

  private async _handleFileUpload(file: File): Promise<void> {
    // Allow multiple uploads; show optimistic pending entry
    const displayName = this.getDisplayName(file.name || (file as any).path || 'file');
    const sizeLabel = this.formatFileSize(file.size || 0);

    const optimisticEntry: FileMetadata = {
      name: displayName,
      storagePath: '',
      note: `Uploading... (${sizeLabel})`,
      size: file.size,
      type: file.type,
      status: 'uploading',
      isUploading: true,
      originalFile: file,
      originalNote: 'Uploaded via onboarding'
    };

    this.pendingUploads = [optimisticEntry, ...this.pendingUploads];
    this.isUploading = true;
    this.syncStatusPolling();
    try {
      await this._uploadService.uploadFile(file, { name: displayName, note: 'Uploaded via onboarding' });
      // Refresh file list
      await this._loadFiles();
      this._notificationService.notify('File uploaded successfully');
      // Remove optimistic entry
      this.pendingUploads = this.pendingUploads.filter(p => p.originalFile !== file);
      // Scroll to web sources section smoothly
      this.scrollToWebSources();
    } catch (error: any) {
      console.error('Upload error:', error);
      this._notificationService.notifyError(error.message || 'Upload failed');
      // mark pending entry as error
      this.pendingUploads = this.pendingUploads.map(p =>
        p.originalFile === file
          ? { ...p, status: 'error', uploadError: error.message || 'Upload failed', isUploading: false, note: 'Upload failed. Try again.' }
          : p
      );
    } finally {
      this.isUploading = false;
    }
  }

  retryPendingUpload(pending: FileMetadata): void {
    if (!pending.originalFile) return;
    // simple retry: attempt upload again
    pending.status = 'uploading';
    pending.uploadError = undefined;
    pending.note = `Retrying upload... (${this.formatFileSize(pending.size || pending.originalFile.size)})`;
    this._handleFileUpload(pending.originalFile as File);
    this.syncStatusPolling();
  }

  dismissPendingError(pending: FileMetadata): void {
    this.pendingUploads = this.pendingUploads.filter(p => p !== pending);
    this.syncStatusPolling();
  }

  async deleteUploadedFile(file: FileMetadata): Promise<void> {
    if (!file.id) return;
    try {
      await this._uploadService.deleteFile(file.id);
      await this._loadFiles();
      this._notificationService.notify('File deleted successfully');
      this.syncStatusPolling();
    } catch (error: any) {
      console.error('Delete error:', error);
      this._notificationService.notifyError(error.message || 'Error deleting file');
    }
  }

  async downloadFile(file: FileMetadata): Promise<void> {
    if (!file.id) return;
    try {
      const blob = await this._uploadService.downloadFile(file.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      this._notificationService.notify('File downloaded successfully');
    } catch (error: any) {
      console.error('Download error:', error);
      this._notificationService.notifyError(error.message || 'Error downloading file');
    }
  }

  private getDisplayName(path: string): string {
    const filename = (path || '').split('/').pop() || '';
    return filename.replace(/^\d+-/, '');
  }

  private formatFileSize(bytes: number): string {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  getStatusClass(status: string): string {
    if (status?.toLowerCase().includes('error') || status?.toLowerCase().includes('failed')) {
      return 'status-failed';
    }
    switch (status?.toLowerCase()) {
      case 'uploaded': return 'status-pending';
      case 'processing': return 'status-processing';
      case 'successfully processed': return 'status-completed';
      case 'completed': return 'status-completed';
      default: return 'status-pending';
    }
  }

  // Poll the backend for status updates when there are pending/processing files
  private syncStatusPolling(): void {
    const hasPending =
      this.uploadedFiles.some(file => this.isPendingStatus(file.status)) ||
      this.pendingUploads.some(file => this.isPendingStatus(file.status));

    if (hasPending) {
      if (!this.statusPolling || this.statusPolling.closed) {
        this.statusPolling = interval(this.statusPollInterval).subscribe(() => {
          void this._loadFiles();
        });
      }
    } else if (this.statusPolling) {
      this.statusPolling.unsubscribe();
      this.statusPolling = undefined;
    }
  }

  private isPendingStatus(status?: string): boolean {
    if (!status) return true;
    const normalized = status.toLowerCase();
    if (normalized.includes('error') || normalized.includes('failed')) return false;
    return !['completed', 'successfully processed', 'processed'].includes(normalized);
  }

  private _isProcessedStatus(status?: string): boolean {
    if (!status) return false;
    const normalized = status.toLowerCase();
    if (normalized.includes('error') || normalized.includes('failed')) return false;
    return ['completed', 'successfully processed', 'processed'].includes(normalized);
  }

  // Expose to template to show processing hint
  get hasProcessingFiles(): boolean {
    return this.uploadedFiles.some(f => this.isPendingStatus(f.status)) || this.pendingUploads.length > 0;
  }

  toggleFreeEmailTooltip(): void {
    this.showFreeEmailTooltip = !this.showFreeEmailTooltip;
  }

  closeFreeEmailTooltip(): void {
    this.showFreeEmailTooltip = false;
  }

  private async _ensureMinimumLoadingTime(startedAt: number): Promise<void> {
    const elapsed = Date.now() - startedAt;
    const remaining = this._minimumActionLoadingMs - elapsed;
    if (remaining > 0) {
      await new Promise(resolve => setTimeout(resolve, remaining));
    }
  }

  private scrollToWebSources(): void {
    // Smooth scroll to the web-sources section after a short delay to allow DOM to update
    setTimeout(() => {
      const element = document.querySelector('.web-sources-card');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  }

  private _restoreProgress(progress: OnboardingProgress): void {
    this.currentStep = progress.current_step || 1;
    if (this.currentStep > this.totalSteps) {
      this.currentStep = this.totalSteps;
    }
    // Don't restore workflows - let user select fresh each time
    this.selectedWorkflows = [];
    this.selectedFramework = progress.framework || null;
    this._applyDefaultPromptFromFramework(this.selectedFramework);

    // Don't pre-populate questions - only show generated ones
  }
}
